import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaHistory, FaStethoscope, FaSearch, FaFolder, FaFolderOpen, FaFileAlt } from "react-icons/fa";
import { MdAccessTime, MdDateRange } from "react-icons/md";
import moment from "moment";
import Swal from 'sweetalert2';

export default function PetHistoryComponent({ appointmentId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petHistories, setPetHistories] = useState([]);
    const [histories, setHistories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingHistory, setEditingHistory] = useState(null);
    const [deletingHistoryId, setDeletingHistoryId] = useState(null);
    const [selectedHistories, setSelectedHistories] = useState([]);
    const [historyNotes, setHistoryNotes] = useState({});
    const [generalNote, setGeneralNote] = useState("");
    const [existingNoteId, setExistingNoteId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [hasHistoryForAppointment, setHasHistoryForAppointment] = useState(false);

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };
    
    useEffect(() => {
        fetchPetHistories();
    }, [petId, appointmentId]);

    const fetchPetHistories = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-history/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.success) {
                const historiesForPet = response.data.petHistory || [];
                setPetHistories(historiesForPet);
                setHistories(response.data.history || []);
                const hasHistory = historiesForPet.some(history => String(history?.appointment_id) === String(appointmentId));
                setHasHistoryForAppointment(hasHistory);
            }
        } catch (error) {
            console.error("Error fetching pet histories:", error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('failed_to_load_histories'),
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('ok')
            });
            setPetHistories([]);
            setHistories([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return {
            date: date.toLocaleDateString(),
            time: date.toLocaleTimeString(),
            full: date.toLocaleString()
        };
    };

    const handleAddHistory = () => {
        setEditingHistory(null);
        setSelectedHistories([]);
        setHistoryNotes({});
        setGeneralNote("");
        setExistingNoteId(null);
        setSearchTerm("");
        setShowModal(true);
    };

    const handleEditHistory = (history) => {
        setEditingHistory(history);

        const historyText = history.history || '';
        const selected = [];
        const notes = {};

        // FIX: Safely extract general note from the note array
        if (history.note && Array.isArray(history.note) && history.note.length > 0 && history.note[0] && history.note[0].note) {
            setGeneralNote(history.note[0].note || "");
            setExistingNoteId(history.note[0].id || null);
        } else {
            setGeneralNote("");
            setExistingNoteId(null);
        }

        if (histories && histories.length > 0) {
            // Parse each line to extract parent and child
            const lines = historyText.split('\n').filter(line => line.trim());

            lines.forEach(line => {
                if (line.includes(':')) {
                    const parts = line.split(':');
                    if (parts.length >= 2) {
                        const parentName = parts[0].trim();
                        const childName = parts[1].trim();
                        const note = parts.length > 2 ? parts.slice(2).join(':').trim() : '';

                        // Find the parent and child
                        const parent = histories.find(p => p.name === parentName);
                        if (parent && parent.child_history) {
                            const child = parent.child_history.find(c => c.name === childName);
                            if (child) {
                                selected.push(child.id);
                                if (note) {
                                    notes[child.id] = note;
                                }
                            }
                        }
                    }
                }
            });
        }

        setSelectedHistories(selected);
        setHistoryNotes(notes);
        setSearchTerm("");
        setShowModal(true);
    };

    const handleDeleteClick = (historyId) => {
        setDeletingHistoryId(historyId);
        setShowDeleteModal(true);
    };

    const handleDeleteHistory = async () => {
        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/pet-history-delete/${deletingHistoryId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.success) {
                setShowDeleteModal(false);
                await fetchPetHistories();
                Swal.fire({
                    icon: 'success',
                    title: t('deleted'),
                    text: t('history_deleted_successfully'),
                    confirmButtonColor: '#3085d6',
                    timer: 2000,
                    showConfirmButton: true
                });
            }
        } catch (error) {
            console.error("Error deleting history:", error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: error.response?.data?.message || t('failed_to_delete_history'),
                confirmButtonColor: '#3085d6'
            });
        } finally {
            setSubmitting(false);
            setDeletingHistoryId(null);
        }
    };

    const handleCheckboxChange = (historyId) => {
        setSelectedHistories(prev => {
            if (prev.includes(historyId)) {
                const newNotes = { ...historyNotes };
                delete newNotes[historyId];
                setHistoryNotes(newNotes);
                return prev.filter(id => id !== historyId);
            } else {
                return [...prev, historyId];
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedHistories || selectedHistories.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: t('warning'),
                text: t('please_select_at_least_one_history'),
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('ok')
            });
            return;
        }

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");

            let historyText = '';

            if (histories && histories.length > 0) {
                histories.forEach(parent => {
                    if (parent && parent.child_history) {
                        parent.child_history.forEach(child => {
                            if (child && selectedHistories.includes(child.id)) {
                                const note = historyNotes[child.id];
                                // Include parent name before child name
                                if (note && note.trim()) {
                                    historyText += `${parent.name}: ${child.name}: ${note}\n`;
                                } else {
                                    historyText += `${parent.name}: ${child.name}\n`;
                                }
                            }
                        });
                    }
                });
            }

            const url = editingHistory
                ? `${process.env.REACT_APP_API_URL}/pet-history-update/${editingHistory.id}`
                : `${process.env.REACT_APP_API_URL}/pet-history`;

            const payload = {
                appointment_id: appointmentId,
                history: historyText.trim(),
            };

            if (generalNote && generalNote.trim()) {
                payload.note = generalNote.trim();  // This is a string, not an array
                if (existingNoteId) {
                    payload.note_id = existingNoteId;
                }
            }

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data.success) {
                setShowModal(false);
                await fetchPetHistories();
                // Swal.fire({
                //     icon: 'success',
                //     title: editingHistory ? t('updated') : t('saved'),
                //     text: editingHistory ? t('history_updated_successfully') : t('history_saved_successfully'),
                //     confirmButtonColor: '#3085d6',
                //     timer: 2000,
                //     showConfirmButton: true
                // });
            }
        } catch (error) {
            console.error("Error saving history:", error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: error.response?.data?.message || t('failed_to_save_history'),
                confirmButtonColor: '#3085d6'
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Function to parse history text and group by parent
    const parseHistoryWithParents = (historyText, allHistories) => {
        if (!historyText || !allHistories) return [];

        const lines = historyText.split('\n').filter(line => line && line.trim());
        const parsedItems = [];

        lines.forEach(line => {
            let parentName = '';
            let childName = '';
            let note = '';

            // Parse format: "Parent Name: Child Name: Note" or "Parent Name: Child Name"
            if (line.includes(':')) {
                const parts = line.split(':');
                if (parts.length >= 2) {
                    parentName = parts[0].trim();
                    childName = parts[1].trim();
                    if (parts.length > 2) {
                        note = parts.slice(2).join(':').trim();
                    }
                }
            } else {
                childName = line.trim();
            }

            // Find which parent this child belongs to
            let parentInfo = null;
            for (const parent of allHistories) {
                if (parent && parent.name === parentName && parent.child_history && parent.child_history.length > 0) {
                    const foundChild = parent.child_history.find(child => child && child.name === childName);
                    if (foundChild) {
                        parentInfo = {
                            parentId: parent.id,
                            parentName: parent.name,
                            childId: foundChild.id,
                            childName: foundChild.name,
                            note: note
                        };
                        break;
                    }
                }

                // Fallback: try to find child without parent name match
                if (!parentInfo && parent && parent.child_history && parent.child_history.length > 0) {
                    const foundChild = parent.child_history.find(child => child && child.name === childName);
                    if (foundChild) {
                        parentInfo = {
                            parentId: parent.id,
                            parentName: parent.name,
                            childId: foundChild.id,
                            childName: foundChild.name,
                            note: note
                        };
                        break;
                    }
                }
            }

            if (parentInfo) {
                parsedItems.push(parentInfo);
            }
        });

        return parsedItems;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading histories')}</p>
            </div>
        );
    }

    const parentHistories = histories && histories.length > 0
        ? histories.filter(h => h && h.parent_id === null)
        : [];

    const filteredParentHistories = parentHistories.filter(parent => {
        if (!parent) return false;
        if (!searchTerm) return true;

        const searchLower = searchTerm.toLowerCase();

        if (parent.name && parent.name.toLowerCase().includes(searchLower)) return true;

        if (parent.child_history && parent.child_history.some(child =>
            child && child.name && child.name.toLowerCase().includes(searchLower)
        )) return true;

        return false;
    });

    const getSelectedHistoriesByParent = () => {
        const grouped = {};

        if (selectedHistories && selectedHistories.length > 0 && histories && histories.length > 0) {
            histories.forEach(parent => {
                if (parent && parent.child_history && parent.child_history.length > 0) {
                    const selectedChildren = parent.child_history.filter(child =>
                        child && selectedHistories.includes(child.id)
                    );

                    if (selectedChildren.length > 0) {
                        grouped[parent.id] = {
                            parentName: parent.name || '',
                            children: selectedChildren.map(child => ({
                                id: child.id,
                                name: child.name || '',
                                note: historyNotes[child.id] || ''
                            }))
                        };
                    }
                }
            });
        }

        return grouped;
    };

    const selectedHistoriesByParent = getSelectedHistoriesByParent();

    return (
        <div>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted">{t('Histories list')}</h6>
                {isAppointmentOpen && !hasHistoryForAppointment &&
                    hasPermission("Appointment History Create") && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAddHistory}
                            className="d-flex align-items-center gap-2"
                        >
                            <FaPlus size={12} /> {t('add history')}
                        </Button>
                    )}
            </div>

            {!petHistories || petHistories.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaHistory size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no histories found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key === openAccordion ? null : key)}
                    className="pet-history-accordion"
                >
                    {petHistories.map((history) => {
                        if (!history) return null;
                        const dateTime = formatDateTime(history.created_at);
                        const parsedHistory = parseHistoryWithParents(history.history, histories);
                        // Extract note text from the note array
                        const noteText = history.note && Array.isArray(history.note) && history.note.length > 0 && history.note[0]
                            ? history.note[0].note
                            : null;

                        return (
                            <Accordion.Item eventKey={String(history.id)} key={history.id} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong>{history.appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>
                                            <div className="text-muted small">
                                                👨‍⚕️<strong>Doctor</strong>: {history.doctor?.user?.name || 'Unknown Doctor'}
                                            </div>
                                            <div className="d-flex align-items-center gap-3 text-muted small">
                                                <span className="d-flex align-items-center gap-1">
                                                    <MdDateRange size={14} />
                                                    {moment(dateTime.date).format("DD MMM YYYY")}
                                                </span>

                                                <span className="d-flex align-items-center gap-1">
                                                    <MdAccessTime size={14} />
                                                    {moment(dateTime.time, "HH:mm:ss").format("hh:mm A")}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                            {hasPermission("Appointment History edit") && (

                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => handleEditHistory(history)}
                                                    className="d-flex align-items-center gap-1"
                                                    style={{ background: '#3182ce', color: '#fff' }}
                                                >
                                                    <FaEdit size={12} />
                                                    <span className="d-none d-sm-inline">{t('edit')}</span>
                                                </Button>
                                            )}
                                            {/* <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDeleteClick(history.id)}
                                                className="d-flex align-items-center gap-1 bg-danger"
                                            >
                                                <FaTrash size={12} />
                                                <span className="d-none d-sm-inline">{t('delete')}</span>
                                            </Button> */}
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white">
                                    <Row className="g-4">
                                        <Col md={12}>
                                            <div className="border-bottom pb-2 mb-3">
                                                <h6 className="mb-0 d-flex align-items-center gap-2">
                                                    <FaStethoscope className="text-primary" />
                                                    {t('history details')}
                                                </h6>
                                            </div>
                                            <div className="p-3 bg-light rounded">
                                                {parsedHistory.length > 0 ? (
                                                    <>
                                                        {(() => {
                                                            const groupedByParent = {};
                                                            parsedHistory.forEach(item => {
                                                                if (!groupedByParent[item.parentId]) {
                                                                    groupedByParent[item.parentId] = {
                                                                        parentName: item.parentName,
                                                                        children: []
                                                                    };
                                                                }
                                                                groupedByParent[item.parentId].children.push({
                                                                    name: item.childName,
                                                                    note: item.note
                                                                });
                                                            });

                                                            return Object.values(groupedByParent).map((group, idx) => (
                                                                <div key={idx} className="mb-3">
                                                                    <div className="d-flex align-items-center gap-2 mb-2">
                                                                        <h6 className="fw-bold text-primary mb-0">{group.parentName}</h6>
                                                                    </div>
                                                                    <div className="ms-4">
                                                                        {group.children.map((child, childIdx) => (
                                                                            <div key={childIdx} className="mb-2">
                                                                                <div className="d-flex align-items-start gap-2">
                                                                                    <div>
                                                                                        <strong className="text-dark">{child.name}</strong>
                                                                                        {child.note && (
                                                                                            <span className="text-muted ms-2">
                                                                                                - {child.note}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ));
                                                        })()}
                                                        {noteText && (
                                                            <div className="mt-3 pt-2 border-top">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <FaFileAlt className="text-success" />
                                                                    <strong className="text-muted">{t('general note')}:</strong>
                                                                </div>
                                                                <p className="mb-0 mt-1 ms-4 text-secondary">{noteText}</p>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="history-content" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                                        {history.history ? (
                                                            history.history.split('\n').map((line, idx) => (
                                                                line && line.trim() && (
                                                                    <div key={idx} className="mb-2">
                                                                        {line.includes(':') ? (
                                                                            <>
                                                                                <strong className="text-primary">{line.split(':')[0]}:</strong>
                                                                                <span className="ms-2 text-secondary">{line.split(':').slice(1).join(':')}</span>
                                                                            </>
                                                                        ) : (
                                                                            <strong className="text-primary">{line}</strong>
                                                                        )}
                                                                    </div>
                                                                )
                                                            ))
                                                        ) : (
                                                            <p className="text-muted mb-0">{t('no history details')}</p>
                                                        )}
                                                        {noteText && (
                                                            <div className="mt-3 pt-2 border-top">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <strong className="text-muted">{t('general note')}:</strong>
                                                                </div>
                                                                <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            {/* Modal with Fixed Height */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="xl"
                style={{ '--bs-modal-width': '1400px' }}
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        {editingHistory ? t('Edit History') : t('Add History')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body style={{ padding: '1rem', height: '550px', overflowY: 'auto' }} className="custom-modal-body">
                        <Row>
                            {/* Left Column: Selection */}
                            <Col md={6}>
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-bold">{t('Select History')}</Form.Label>
                                    <div className="position-relative">
                                        <Form.Control
                                            type="text"
                                            placeholder={t('Search history...')}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="mb-2"
                                            size="sm"
                                        />
                                        <FaSearch className="position-absolute" style={{ right: '10px', top: '8px', color: '#999', fontSize: '12px' }} />
                                    </div>
                                    <div
                                        className="border rounded p-2"
                                        style={{ height: '280px', overflowY: 'auto', backgroundColor: '#fff' }}
                                    >
                                        {filteredParentHistories.length > 0 ? (
                                            filteredParentHistories.map(parent => (
                                                <div key={parent.id} className="mb-2">
                                                    <h6 className="fw-bold text-secondary mb-1" style={{ fontSize: '13px' }}>{parent.name}</h6>
                                                    {parent.child_history && parent.child_history.length > 0 ? (
                                                        parent.child_history
                                                            .filter(child => {
                                                                if (!searchTerm) return true;
                                                                return child.name.toLowerCase().includes(searchTerm.toLowerCase());
                                                            })
                                                            .map(child => (
                                                                <div key={child.id} className="ms-2 mb-1">
                                                                    <Form.Check
                                                                        type="checkbox"
                                                                        id={`child-${child.id}`}
                                                                        checked={selectedHistories.includes(child.id)}
                                                                        onChange={() => handleCheckboxChange(child.id)}
                                                                        label={child.name}
                                                                        style={{ fontSize: '13px' }}
                                                                    />
                                                                </div>
                                                            ))
                                                    ) : (
                                                        <p className="text-muted small ms-2">No child histories available</p>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-muted text-center">{t('No matching histories found')}</p>
                                        )}
                                    </div>
                                </Form.Group>
                            </Col>

                            {/* Right Column: Preview/Selected */}
                            <Col md={6}>
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-bold">{t('Selected History')}</Form.Label>
                                    <div
                                        className="border rounded p-2 bg-white"
                                        style={{ height: '280px', overflowY: 'auto' }}
                                    >
                                        {Object.keys(selectedHistoriesByParent).length > 0 ? (
                                            Object.values(selectedHistoriesByParent).map((group, index) => (
                                                <div key={index} className="mb-2">
                                                    <h6 className="fw-bold text-primary mb-1" style={{ fontSize: '13px' }}>{group.parentName}</h6>
                                                    {group.children.map((child, childIndex) => (
                                                        <div key={childIndex} className="ms-2 mb-1">
                                                            <div className="text-dark" style={{ fontSize: '13px' }}>
                                                                • {child.name}
                                                                {child.note && (
                                                                    <span className="text-muted ms-1">
                                                                        - {child.note}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-muted text-center">{t('No history selected')}</p>
                                        )}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* General Note Field */}
                        <Form.Group className="mt-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaFileAlt /> {t('General Note')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                                placeholder={t('Add any additional notes here...')}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer style={{ padding: '0.75rem' }} className="custom-modal-footer">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowModal(false)}
                        >
                            {t('Close')}
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            size="sm"
                            disabled={submitting}
                        >
                            {submitting ? <Spinner size="sm" /> : t('Submit')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">{t('confirm_delete')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="custom-modal-body">
                    <FaTrash size={48} className="text-danger mb-3" />
                    <h5>{t('delete_history_confirmation')}</h5>
                    <p className="text-muted mb-0">{t('this_action_cannot_be_undone')}</p>
                </Modal.Body>
                <Modal.Footer className="custom-modal-footer">
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>{t('cancel')}</Button>
                    <Button variant="danger" onClick={handleDeleteHistory} disabled={submitting}>
                        {submitting ? t('deleting') : t('delete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}